import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useEffect } from 'react'
import { authService, useAuth } from '@bri/shared-components'
import { hasCompletedWorkspaceOnboarding } from '@/lib/onboarding/workspaceOnboarding'

// Import pages
import DashboardPage from './pages/DashboardPage'
import LoginPage from './pages/LoginPage'
import SignupPage from './pages/SignupPage'
import VerifyEmailPage from './pages/VerifyEmailPage'
import VerifyCodePage from './pages/VerifyCodePage'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
import ProjectsPage from './pages/ProjectsPage'
import TeamPage from './pages/TeamPage'
import CreateWorkspacePage from './pages/CreateWorkspacePage'
import SettingsPage from './pages/SettingsPage'
import BillingPage from './pages/BillingPage'
import ProfilePage from './pages/ProfilePage'
import WorkspacesPage from './pages/WorkspacesPage'
import OnboardingPage from './pages/OnboardingPage'

function App() {
  const { user, isLoading } = useAuth()
  const location = useLocation()

  useEffect(() => {
    const onError = (event: ErrorEvent) => {
      void authService.sendFrontendTelemetry('frontend.error', 'user-dashboard', {
        message: event.message,
        source: event.filename,
        line: event.lineno,
        column: event.colno,
      })
    }

    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      void authService.sendFrontendTelemetry('frontend.unhandled_rejection', 'user-dashboard', {
        reason: typeof event.reason === 'string' ? event.reason : String(event.reason),
      })
    }

    window.addEventListener('error', onError)
    window.addEventListener('unhandledrejection', onUnhandledRejection)
    return () => {
      window.removeEventListener('error', onError)
      window.removeEventListener('unhandledrejection', onUnhandledRejection)
    }
  }, [])

  // Debug logging (dev only)
  if (import.meta.env.VITE_ENVIRONMENT === 'dev') {
    console.log('App render - user:', user, 'isLoading:', isLoading)
  }

  // Show loading state
  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="loading-spinner w-8 h-8 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  // If user is not authenticated, show auth routes
  if (!user) {
    return (
      <Routes>
        <Route path="/auth/login" element={<LoginPage />} />
        <Route path="/auth/signup" element={<SignupPage />} />
        <Route path="/auth/verify-email" element={<VerifyEmailPage />} />
        <Route path="/auth/verify-code" element={<VerifyCodePage />} />
        <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="*" element={<Navigate to="/auth/login" replace />} />
      </Routes>
    )
  }

  const shouldShowOnboarding = !hasCompletedWorkspaceOnboarding(user.email)
  const isAuthRoute = location.pathname.startsWith('/auth')
  const isOnboardingRoute = location.pathname === '/app/onboarding'
  const onboardingReturnPath =
    location.pathname === '/'
      ? '/app'
      : `${location.pathname}${location.search}${location.hash}`

  if (shouldShowOnboarding && !isAuthRoute && !isOnboardingRoute) {
    return <Navigate to="/app/onboarding" replace state={{ from: onboardingReturnPath }} />
  }

  // User is authenticated, show the main app
  return (
    <div className="h-full bg-gray-50 antialiased font-sans">
      <Routes>
        {/* Redirect root to dashboard */}
        <Route path="/" element={<Navigate to="/app" replace />} />
        
        {/* Auth routes - redirect to app if already authenticated */}
        <Route path="/auth/login" element={<Navigate to="/app" replace />} />
        <Route path="/auth/signup" element={<Navigate to="/app" replace />} />
        
        {/* App routes */}
        <Route path="/app" element={<DashboardPage />} />
        <Route path="/app/projects/*" element={<ProjectsPage />} />
        <Route path="/app/team/*" element={<TeamPage />} />
        <Route path="/app/settings" element={<SettingsPage />} />
        <Route path="/app/billing" element={<BillingPage />} />
        <Route path="/app/profile" element={<ProfilePage />} />
        <Route path="/app/workspaces" element={<WorkspacesPage />} />
        <Route path="/app/workspaces/new" element={<CreateWorkspacePage />} />
        <Route path="/app/onboarding" element={<OnboardingPage />} />
        
        {/* Catch all - redirect to dashboard */}
        <Route path="*" element={<Navigate to="/app" replace />} />
      </Routes>
    </div>
  )
}

export default App
